import { useCallback } from "react";
import {
  isMunicipalVio,
  isTicketWarning,
  isTicketNumberValid,
} from "../../utils/utils";
import { INCIDENT_ACTIONS } from "../../Actions/IncidentActions";
import * as Sentry from "@sentry/react";
import { TICKET_ACTIONS } from "../../Actions/TicketActions";

export const useHandlePrint = (
  ticket,
  printCallback,
  setStatusCallback,
  submitCallback,
  online,
  preparePrintCallback,
) => {
  if (!ticket) {
    throw new Error("Missing argument: ticket in useHandlePrint");
  }
  if (typeof printCallback !== "function") {
    throw new Error("Missing function: printCallback in useHandlePrint");
  }
  if (typeof setStatusCallback !== "function") {
    throw new Error("Missing function: setStatusCallback in useHandlePrint");
  }
  if (typeof submitCallback !== "function") {
    throw new Error("Missing function: submitCallback in useHandlePrint");
  }
  if (typeof online !== "boolean") {
    throw new Error("Missing boolean: online in useHandlePrint");
  }

  return useCallback(async () => {
    try {
      await preparePrintCallback?.();

      if (isTicketWarning(ticket?.ticketViolationPrototype)) {
        printCallback();
        return {
          success: true,
          error: null,
          message: "Warning printed successfully",
        };
      } else {
        if (ticket?.currentStatus <= 3) {
          const status = online
            ? await setStatusCallback(3)
            : setStatusCallback(3);
          if (
            !isTicketWarning(ticket?.ticketViolationPrototype) &&
            !isMunicipalVio(ticket?.ticketViolationPrototype) &&
            online &&
            !ticket?.ticketViolationPrototype?.doNotSubmit &&
            window.confirm(
              "The ticket has not been submitted. Would you like to submit it now?",
            )
          ) {
            try {
              printCallback();
              const { success, error, message } = await submitCallback(status);
              return { success: success, message: message, error: error };
            } catch (err) {
              Sentry.captureException(err);
              console.error(err);
              // handle error here
              return { success: false, error: err, message: null };
            }
          } else {
            if (ticket?.ticketViolationPrototype?.doNotSubmit) {
              window.alert(
                "This violation for this ticket cannot be submitted to the state.",
              );
            }
            printCallback();
            return {
              success: true,
              error: null,
              message: "Ticket printed successfully",
            };
          }
        } else {
          printCallback();
          return {
            success: true,
            error: null,
            message: "Ticket printed successfully",
          };
        }
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error(error);
      return { success: false, error: error, message: null };
    }
  }, [
    ticket?.ticketViolationPrototype,
    ticket?.currentStatus,
    printCallback,
    online,
    setStatusCallback,
    submitCallback,
    preparePrintCallback,
  ]);
};

// export const useAddAuditLogEntry = () = >
export const useSetCurrentStatus = (
  ticket,
  incidentId,
  user,
  handleDispatch,
  updateTicketCurrentStatus,
  online,
) => {
  const { UPDATE_INCIDENT_TICKET } = INCIDENT_ACTIONS;
  const { UPDATE_TICKET } = TICKET_ACTIONS;

  return useCallback(
    async (statusCode, dispatchOnly = false) => {
      if (!statusCode) {
        throw new Error("Missing argument: statusCode");
      }
      if (!ticket) {
        throw new Error("Missing argument: ticket");
      }
      if (!incidentId) {
        throw new Error("Missing argument: incidentId");
      }
      if (!user?.id) {
        throw new Error("Missing argument: user.id");
      }
      if (!handleDispatch) {
        throw new Error("Missing argument: handleDispatch");
      }
      if (!updateTicketCurrentStatus) {
        throw new Error("Missing argument: updateTicketCurrentStatus");
      }
      if (typeof online !== "boolean") {
        throw new Error("Missing boolean: online in useHandlePrint");
      }
      try {
        if (ticket?.currentStatus !== statusCode) {
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId,
            ticketId: ticket?.id || ticket?.uuid,
            property: "currentStatus",
            value: statusCode,
          });
          if (dispatchOnly) return statusCode;
          try {
            if (online) {
              const res = await updateTicketCurrentStatus({
                variables: {
                  input: {
                    incidentId: incidentId,
                    originatorId: user?.id,
                    officerId: ticket?.officer?.id,
                    ticketId: ticket.id ? ticket.id : ticket.uuid,
                    currentStatus: statusCode,
                  },
                },
                context: { serializationKey: "incidentQueue" },
              });
              const status =
                res.data.addOrUpdateTicketInIncident.ticket.currentStatus;
              handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
                incidentId: incidentId,
                ticketId: ticket?.id || ticket?.uuid,
                property: "currentStatus",
                value: status,
              });
              return status;
            } else {
              updateTicketCurrentStatus({
                variables: {
                  input: {
                    incidentId: incidentId,
                    originatorId: user?.id,
                    officerId: ticket?.officer?.id,
                    ticketId: ticket.id ? ticket.id : ticket.uuid,
                    currentStatus: statusCode,
                  },
                },
                context: { serializationKey: "incidentQueue" },
              });
              const status = statusCode;
              handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
                incidentId: incidentId,
                ticketId: ticket?.id || ticket?.uuid,
                property: "currentStatus",
                value: status,
              });
              return status;
            }
          } catch (error) {
            Sentry.captureException(error);
            console.error(error);
          }
        }
      } catch (error) {
        Sentry.captureException(error);
        console.error(error);
        return { success: false, error: error, message: null };
      }
    },
    [
      UPDATE_INCIDENT_TICKET,
      UPDATE_TICKET,
      handleDispatch,
      incidentId,
      ticket,
      updateTicketCurrentStatus,
      user,
      online,
    ],
  );
};

export const useHandleTicketSubmission = (
  ticket,
  canSubmitToJb,
  user,
  isMunicipalVio,
  isSuperUser,
  submitCitation,
  setStatusCallback,
  online,
) => {
  let missingArgs = [];
  if (!ticket) missingArgs.push("ticket");
  if (canSubmitToJb === undefined || canSubmitToJb === null)
    missingArgs.push("canSubmitToJb");
  if (!user) missingArgs.push("user");
  if (!isMunicipalVio) missingArgs.push("isMunicipalVio");
  if (!isSuperUser) missingArgs.push("isSuperUser");
  if (!submitCitation) missingArgs.push("submitCitation");
  if (!setStatusCallback) missingArgs.push("setStatusCallback");
  if (typeof online !== "boolean") missingArgs.push("online");

  if (missingArgs.length > 0) {
    throw new Error(
      `Missing argument(s) for useHandleTicketSubmission: ${missingArgs.join(
        ", ",
      )}`,
    );
  }
  return useCallback(
    async localStatus => {
      try {
        if (canSubmitToJb === false) {
          // alert('You do not have sufficient permissions to submit E-Tickets')
          return {
            success: false,
            error: new Error(
              "You do not have sufficient permissions to submit E-Tickets",
            ),
          };
        } else {
          if (isMunicipalVio(ticket?.ticketViolationPrototype)) {
            // alert('This is a municipal violation and cannot be submitted')
            return {
              success: false,
              error: new Error(
                "This is a municipal violation and cannot be submitted",
              ),
            };
          }
          if (ticket?.ticketViolationPrototype?.doNotSubmit) {
            return {
              success: false,
              error: new Error(
                "This violation for this ticket cannot be submitted to the state.",
              ),
            };
          }

          if (
            ticket?.currentStatus < 3 &&
            localStatus !== undefined &&
            localStatus < 3
          ) {
            // alert('Please print the ticket before submitting.')
            return {
              success: false,
              error: new Error("Please print the ticket before submitting."),
            };
          } else {
            if (ticket?.currentStatus >= 4) {
              if (isSuperUser(user?.roleNames)) {
                if (!online) {
                  return {
                    success: false,
                    error: new Error(
                      "Ticket resubmission can only be done while online.",
                    ),
                  };
                }
                if (
                  window.confirm(
                    `Ticket ${ticket?.ticketNumber} has already been submitted. Would you like to resubmit it?`,
                  )
                ) {
                  const res = await submitCitation({
                    variables: {
                      input: {
                        ticketId: ticket?.id,
                        officerId: user?.id,
                        resubmitAnyway: true,
                      },
                    },
                    context: { serializationKey: "incidentQueue" },
                  });
                  const status =
                    res?.data?.submitCitation?.ticket?.currentStatus;
                  if (status) setStatusCallback(status, true);
                  if (
                    res?.data?.submitCitation?.errors?.length === 0 ||
                    !res?.data?.submitCitation?.errors
                  ) {
                    return {
                      success: true,
                      error: null,
                      message: "Ticket resubmitted successfully",
                    };
                  } else {
                    const errorMessage =
                      "There was an issue submitting your ticket.\n" +
                      res?.data?.submitCitation?.errors?.join("\n");
                    return {
                      success: false,
                      error: new Error(errorMessage),
                      message: null,
                    };
                  }
                }
              } else {
                // alert(`Ticket ${ticket?.ticketNumber} has already been submitted. You do not have sufficient roles to resubmit the ticket.`)
                return {
                  success: false,
                  error: new Error(
                    `Ticket ${ticket?.ticketNumber} has already been submitted. You do not have sufficient roles to resubmit the ticket.`,
                  ),
                };
              }
            } else {
              //Ticket status is 3 - "printed"
              if (isTicketNumberValid(ticket?.ticketNumber)) {
                if (online) {
                  await setStatusCallback(4);
                  const res = await submitCitation({
                    variables: {
                      input: {
                        ticketId: ticket?.id,
                        officerId: user?.id,
                        resubmitAnyway: false,
                      },
                    },
                    context: { serializationKey: "incidentQueue" },
                  });
                  const status =
                    res?.data?.submitCitation?.ticket?.currentStatus;
                  if (status) await setStatusCallback(status, true);
                  if (
                    res?.data?.submitCitation?.errors?.length === 0 ||
                    !res?.data?.submitCitation?.errors
                  ) {
                    return {
                      success: true,
                      error: null,
                      message: "Ticket submitted successfully",
                    };
                  } else {
                    const errorMessage =
                      "There was an issue submitting your ticket.\n" +
                      res?.data?.submitCitation?.errors?.join("\n");
                    return { success: false, error: new Error(errorMessage) };
                  }
                } else {
                  //offline path
                  setStatusCallback(4, true);
                  submitCitation({
                    variables: {
                      input: {
                        ticketId: ticket?.id,
                        officerId: user?.id,
                        resubmitAnyway: false,
                      },
                    },
                    context: { serializationKey: "incidentQueue" },
                  });
                  return {
                    success: true,
                    error: null,
                    message: "Ticket will be submitted when online.",
                  };
                }
              } else {
                // alert('The ticket number is invalid. Please correct it before submitting. This correction may require you to contact your Program Manager for assistance. If this ticket has been handed to the defendant changing the ticket number will require a 702 amendment form.')
                return {
                  success: false,
                  error: new Error(
                    "The ticket number is invalid. Please correct it before submitting. This correction may require you to contact your Program Manager for assistance. If this ticket has been handed to the defendant changing the ticket number will require a 702 amendment form.",
                  ),
                };
              }
            }
          }
        }
      } catch (error) {
        Sentry.captureException(error);
        console.error(error);
        return { success: false, error: error, message: null };
      }
    },
    [
      canSubmitToJb,
      isMunicipalVio,
      isSuperUser,
      setStatusCallback,
      submitCitation,
      ticket,
      user,
      online,
    ],
  );
};
